import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, createSearchParams } from 'react-router-dom'
import { ProtectedPage } from '~/src/components/templates'
import { WishListForm } from '~/src/components/pages/WishList/components/WishListForm'
import { WishListTable } from '~/src/components/pages/WishList/components/WishListTable'
import { LoadingNormal } from '~/src/components/organisms/Loading/LoadingNormal'
import { useMessage } from '~/src/hooks'
import { useWishListHooks } from './hooks/useWishListHooks'
import {
  IWishListParams,
  IWishListRow,
  IWishListResponse,
  withWishListPaginationParams,
} from '~/src/repositories/WishList/models/WishList'

export type { IWishListParams } from '~/src/repositories/WishList/models/WishList'

export const WishList: React.FC = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const { handleOpenWithErrorMessage } = useMessage()
  const [wishRows, setWishRows] = useState<IWishListRow[]>()
  const [tablePagination, setTablePagination] = useState<IWishListResponse['pagination']>()

  const [params, setParams] = useState<IWishListParams>(() =>
    withWishListPaginationParams(
      // @ts-ignore
      location.state?.prevState
        ? // @ts-ignore
          (location.state.prevState as IWishListParams)
        : (location.state as IWishListParams | null) ?? undefined
    )
  )

  const { wishList, wishListError, wishListMutate, setBrowserBackStateAtom } = useWishListHooks(params)

  useEffect(() => {
    if (wishListError) handleOpenWithErrorMessage(wishListError.message)
  }, [wishListError, handleOpenWithErrorMessage])

  useEffect(() => {
    // @ts-ignore
    if (location.state && location.state.prevState) {
      // @ts-ignore
      const prevState = location.state.prevState || undefined
      setParams(withWishListPaginationParams(prevState as IWishListParams))
    } else {
      const state = (location.state as IWishListParams | null) ?? undefined
      setParams(withWishListPaginationParams(state))
      setWishRows(undefined)
      setBrowserBackStateAtom({ page: '', text: '' })
    }
  }, [navigate])

  useEffect(() => {
    if (wishList) {
      if (wishList.pagination) {
        setTablePagination(wishList.pagination)
      }
      wishListMutate().then((r) => {
        setWishRows(r?.data)
        if (r?.pagination) {
          setTablePagination(r.pagination)
        }
      })
    }
  }, [wishList])

  const handleSearch = async (values: IWishListParams) => {
    const nextParams = withWishListPaginationParams({ ...values, offset: 1 })
    setParams(nextParams)
    navigate(
      {
        pathname: location.pathname,
        search: `?${createSearchParams(nextParams as Record<string, string>)}`,
      },
      { replace: true, state: nextParams }
    )
  }

  const fetchWishList = async (_params?: IWishListParams): Promise<void> => {
    let mergedParams = _params
    if (location.state) {
      // @ts-ignore
      mergedParams = { ...location.state, ..._params }
    }
    const nextParams = withWishListPaginationParams(mergedParams)
    setParams(nextParams)
    navigate(
      {
        pathname: location.pathname,
        // @ts-ignore
        search: `?${createSearchParams(nextParams)}`,
      },
      { state: nextParams }
    )
  }

  if (!wishRows) return <LoadingNormal />

  return (
    <ProtectedPage pageTitle={t('wish_list.title')}>
      <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
        <WishListForm onSearch={handleSearch} />
        <WishListTable
          data={wishRows}
          pagination={tablePagination}
          onNextPages={fetchWishList}
          params={params}
        />
      </Box>
    </ProtectedPage>
  )
}
